import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { mapUIActionsForRequest } from 'src/graphql/entities/behavior';
import { parseJsonSafely } from 'src/utils/objects';
import { FetchedMacroRaw, Macro, MacroAction } from './macro';
import {
  MacrosFormFields,
  MacrosFormModel,
  MacroSnippetSource,
  MacroVisibility,
  MacroWriteRequest,
} from './macro.types';

/**
 * Derives the snippet source from the stored columns. A macro carrying either
 * prompt column is an AI-prompt macro; everything else — including every macro
 * that existed before this feature — is static text.
 */
export function resolveMacroSnippetSource(
  macro: Pick<Macro, 'aiPromptId' | 'aiPromptText'>,
): MacroSnippetSource {
  return macro.aiPromptId || macro.aiPromptText
    ? MacroSnippetSource.AiPrompt
    : MacroSnippetSource.StaticText;
}

export function mapFormValuesToMacrosRequest(
  values: MacrosFormModel,
  partnerId: number,
): MacroWriteRequest {
  /**
   * Every action for now can only have a single rule, although it is using
   * react-querybuider component which creates groups of rules, allowing
   * to have multiple rules for a single action.
   */
  const requestActions = values.actions.map(mapUIActionsForRequest);
  const usesAiPrompt = values[MacrosFormFields.SnippetSource] === MacroSnippetSource.AiPrompt;
  // Only the chosen branch is persisted, so a macro is never both static text
  // and an AI prompt — whatever the other branch still holds in the form.
  const aiPromptId = usesAiPrompt ? values.aiPromptId || null : null;
  const aiPromptText = usesAiPrompt && !aiPromptId ? values.aiPromptText || null : null;

  return {
    partnerId,
    name: values.name,
    description: values.description,
    template: usesAiPrompt ? '' : values.template,
    aiPromptId,
    aiPromptText,
    actions: JSON.stringify(requestActions),
    messagePrivacy: values.messagePrivacy,
    public: values[MacrosFormFields.Visibility] !== MacroVisibility.OnlyMe,
  };
}

export function mapMacroResponseToUI(macro: FetchedMacroRaw): Macro {
  const conversationAssigneeGroups = (macro.conversationAssigneeGroups || []).map((group) => ({
    id: group.id,
    value: group.id,
    label: group.name,
    partnerId: macro.partnerId,
  }));

  return {
    id: macro.macroId,
    name: macro.name,
    description: macro.description,
    template: macro.template,
    aiPromptId: macro.aiPromptId ?? null,
    aiPromptText: macro.aiPromptText ?? null,
    public: macro.public,
    actions: parseJsonSafely<MacroAction[]>(macro.actions) || [],
    messagePrivacy: macro.messagePrivacy,
    isDefault: macro.partnerId === PLATFORM_PARTNER_ID,
    partnerId: macro.partnerId,
    createdBy: macro.createdBy,
    conversationAssigneeGroups,
    createdAt: macro.createdAt,
    updatedAt: macro.updatedAt,
  };
}
